import { IconButton, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import useBannerMessages from "../../../../hooks/usebannerMessage";
import { useLocation } from "react-router-dom";
import { useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";

const responsive = {
  superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
};

const useStyles = makeStyles(() => ({
  root: (props) => ({
    width: "100%",
    minHeight: 30,
    backgroundColor: props?.bgColor ?? "#FFE300",
    display: "flex",
    alignItems: "center",
    padding: "10px 0",
  }),
  closeButton: (props) => ({
    cursor: "pointer",
    color: props?.textColor ?? "black",
    marginRight: 5,
  }),
  text: (props) => ({
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: props?.textColor ?? "black",
    width: "100%",
    minHeight: 30,
    height: "100%",
  }),
  dotList: { bottom: 20 },
}));

const uniqueKey = (m) =>
  (m && (m.id ?? m.bannerMessageId ?? m.messageId)) ?? null;

const pickContrast = (hex) => {
  if (!hex) return "#000";
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean.split("").map((c) => c + c).join("")
      : clean;

  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return "#000";

  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 186 ? "#000" : "#FFF";
};

const getDurationMs = (msg) => {
  const raw = msg?.durationInSeconds ?? msg?.DurationInSeconds;
  const n = typeof raw === "string" ? parseFloat(raw) : raw;
  return Number.isFinite(n) && n > 0 ? Math.round(n * 1000) : 5000;
};

function BannerMessageContainer({ obj, handleClose, finalStyle, classes }) {
  return (
    <>
      <span />
      <span style={finalStyle} dangerouslySetInnerHTML={{ __html: obj.message }} />
      {obj.closeable ? (
        <IconButton className={classes.closeButton} onClick={() => handleClose(obj)}>
          <CloseIcon />
        </IconButton>
      ) : (
        <span />
      )}
    </>
  );
}

export default function BannerCarousel(props) {
  const { bannerMessages } = useBannerMessages();
  const location = useLocation();

  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const lg = useMediaQuery(theme.breakpoints.up("lg"));

  const finalStyle = useMemo(() => {
    if (sm) return { fontSize: "10.5px" };
    if (lg) return { width: "60%" };
    return {};
  }, [sm, lg]);

  const [hiddenMessages, setHiddenMessages] = useState([]);
  const [index, setIndex] = useState(0);
  const carouselRef = useRef(null);

  // Reset hidden on route change (your existing behavior)
  useEffect(() => {
    setHiddenMessages([]);
    setIndex(0);
  }, [location.pathname]);

  const handleClose = useCallback((message) => {
    const k = uniqueKey(message);
    if (k == null) return;
    setHiddenMessages((prev) => (prev.includes(k) ? prev : [...prev, k]));
  }, []);

  // Load hidden from localStorage (with 24h expiry)
  useEffect(() => {
    const storedDate = localStorage.getItem("hiddenMessagesAtDate");
    const last = storedDate ? new Date(storedDate) : null;
    const oneDayPassed = last ? Date.now() - last.getTime() >= 86400000 : true;

    if (oneDayPassed) {
      localStorage.setItem("hiddenMessages", JSON.stringify([]));
      localStorage.removeItem("hiddenMessagesAtDate");
      setHiddenMessages([]);
      return;
    }

    try {
      const stored = JSON.parse(localStorage.getItem("hiddenMessages") || "[]");
      setHiddenMessages(Array.isArray(stored) ? stored.filter((v) => v != null) : []);
    } catch {
      setHiddenMessages([]);
    }
  }, []);

  // Persist hidden to localStorage
  useEffect(() => {
    const clean = hiddenMessages.filter((v) => v != null);
    localStorage.setItem("hiddenMessages", JSON.stringify(clean));

    const exists = localStorage.getItem("hiddenMessagesAtDate");
    if (clean.length > 0 && !exists) {
      localStorage.setItem("hiddenMessagesAtDate", new Date().toString());
    }
    if (clean.length === 0 && exists) {
      localStorage.removeItem("hiddenMessagesAtDate");
    }
  }, [hiddenMessages]);

  const visibleMessages = useMemo(() => {
    return (bannerMessages || []).filter((msg) => {
      const k = uniqueKey(msg);
      return k == null ? true : !hiddenMessages.includes(k);
    });
  }, [bannerMessages, hiddenMessages]);

  // Keep index valid when list changes (close message, new messages, etc.)
  useEffect(() => {
    if (visibleMessages.length === 0) return;
    setIndex((i) => Math.min(i, visibleMessages.length - 1));
  }, [visibleMessages.length]);

  const currentMsg = visibleMessages[index] ?? null;
  const bg = currentMsg?.backgroundColorHexCode ?? "#FFE300";
  const text = pickContrast(bg);
  const classes = useStyles({ bgColor: bg, textColor: text });

  // Per-message timer (single source of truth: index)
  useEffect(() => {
    if (visibleMessages.length <= 1) return;
    const ms = getDurationMs(currentMsg);

    const t = setTimeout(() => {
      setIndex((i) => (i + 1) % visibleMessages.length);
    }, ms);

    return () => clearTimeout(t);
  }, [index, visibleMessages.length, currentMsg]);

  // Sync carousel to our index (if carousel exposes goToSlide)
  useEffect(() => {
    const api = carouselRef.current;
    if (!api) return;
    if (typeof api.goToSlide === "function") api.goToSlide(index);
  }, [index]);

  if (visibleMessages.length === 0) return null;

  if (visibleMessages.length === 1) {
    const message = visibleMessages[0];
    return (
      <div className={classes.root}>
        <div className={classes.text}>
          <BannerMessageContainer
            obj={message}
            handleClose={handleClose}
            finalStyle={finalStyle}
            classes={classes}
          />
        </div>
      </div>
    );
  }

  return (
    <Carousel
      swipeable
      draggable={false}
      responsive={responsive}
      infinite={false}         // we handle looping with index math
      autoPlay={false}         // we handle timing ourselves
      keyBoardControl
      removeArrowOnDeviceType={["tablet", "mobile"]}
      deviceType={props.deviceType}
      className={classes.root}
      dotListClass={classes.dotList}
      ref={carouselRef}
      afterChange={(_, state) => {
        // When user swipes, update our index.
        const i = state?.currentSlide ?? 0;
        const normalized = visibleMessages.length ? i % visibleMessages.length : 0;
        setIndex(normalized);
      }}
    >
      {visibleMessages.map((obj, i) => (
        <div key={uniqueKey(obj) ?? i} className={classes.text}>
          <BannerMessageContainer
            obj={obj}
            handleClose={handleClose}
            finalStyle={finalStyle}
            classes={classes}
          />
        </div>
      ))}
    </Carousel>
  );
}
